import { useEffect, useState, useCallback } from "react";
import { StyleSheet, View, ActivityIndicator, Pressable, RefreshControl } from "react-native";
import { router, usePathname } from "expo-router";
import { DrawerContentScrollView } from "@react-navigation/drawer";
import { ThemedText } from "@/components/ThemedText";
import { useTheme } from "@/hooks/useTheme";
import { twilioSmsService, SMSConversation } from "@/services/twilioSmsService";
import { formatDistanceToNow } from "date-fns";
import { formatPhoneNumber } from "@/lib/phone";
import { userService } from "@/services/userService";
import { useSMSRealtime } from "@/hooks/useSMSRealtime";
import Avatar from "@/components/Avatar";

type MatchedUser = {
  id: string;
  name: string;
};

// Normalize phone to 10-digit format (strip +1 or leading 1)
function normalizeToTenDigits(phone: string): string {
  const digits = phone.replace(/\D/g, "");
  // If 11 digits starting with 1, strip the country code
  if (digits.length === 11 && digits.startsWith("1")) {
    return digits.slice(1);
  }
  return digits;
}

interface SMSDrawerContentProps {
  phoneNumber: string;
}

export function SMSDrawerContent({ phoneNumber }: SMSDrawerContentProps) {
  const { colors } = useTheme();
  const pathname = usePathname();
  const [conversations, setConversations] = useState<SMSConversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [usersByPhone, setUsersByPhone] = useState<Map<string, MatchedUser>>(new Map());

  // Look up users by their cell phone numbers
  useEffect(() => {
    if (conversations.length === 0) return;
    let cancelled = false;
    (async () => {
      const users = await userService.getUsersByCellPhones(conversations.map((c) => c.externalNumber));
      if (cancelled) return;
      const phoneMap = new Map<string, MatchedUser>();
      for (const u of users) {
        phoneMap.set(u.last10, {
          id: u.id,
          name: u.preferred_name || `${u.first_name || ""} ${u.last_name || ""}`.trim(),
        });
      }
      setUsersByPhone(phoneMap);
    })();
    return () => {
      cancelled = true;
    };
  }, [conversations]);

  // Helper to get matched user for a phone number
  const getMatchedUser = useCallback(
    (externalNumber: string): MatchedUser | null => {
      const normalized = normalizeToTenDigits(externalNumber);
      return usersByPhone.get(normalized) || null;
    },
    [usersByPhone],
  );

  const fetchConversations = useCallback(async () => {
    if (!phoneNumber) return;

    try {
      setError(null);
      const data = await twilioSmsService.getConversations(phoneNumber);
      setConversations(data);
    } catch (err: any) {
      console.error("Failed to fetch SMS conversations:", err);
      setError(err.message || "Failed to load conversations");
    }
  }, [phoneNumber]);

  useEffect(() => {
    setLoading(true);
    fetchConversations().finally(() => setLoading(false));
  }, [fetchConversations]);

  // Keep the conversation list (last message, ordering, unread) live on inbound SMS.
  useSMSRealtime({
    phoneNumber,
    onNewMessage: () => {
      fetchConversations();
    },
    enabled: !!phoneNumber,
  });

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await fetchConversations();
    setRefreshing(false);
  }, [fetchConversations]);

  const handleConversationPress = (externalNumber: string) => {
    const encodedPhone = encodeURIComponent(phoneNumber);
    const encodedExternal = encodeURIComponent(externalNumber);
    router.push(`/chat/sms/${encodedPhone}/${encodedExternal}`);
  };

  // Extract current conversation from pathname
  const getCurrentConversation = () => {
    const match = pathname.match(/\/chat\/sms\/[^/]+\/([^/]+)/);
    if (match) {
      return decodeURIComponent(match[1]);
    }
    return null;
  };

  const currentConversation = getCurrentConversation();

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="small" color={colors.primary} />
        <ThemedText style={[styles.loadingText, { color: colors.placeholder }]}>Loading...</ThemedText>
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.errorContainer}>
        <ThemedText style={[styles.errorText, { color: colors.dangerText }]}>{error}</ThemedText>
      </View>
    );
  }

  if (conversations.length === 0) {
    return (
      <View style={styles.emptyContainer}>
        <ThemedText style={[styles.emptyText, { color: colors.placeholder }]}>No conversations</ThemedText>
      </View>
    );
  }

  return (
    <DrawerContentScrollView
      contentContainerStyle={{ gap: 4 }}
      refreshControl={<RefreshControl refreshing={refreshing} onRefresh={handleRefresh} tintColor={colors.primary} />}
    >
      {conversations.map((conv) => {
        const isActive = currentConversation === conv.externalNumber;
        const hasUnread = conv.unread;
        const matchedUser = getMatchedUser(conv.externalNumber);

        return (
          <Pressable
            key={conv.externalNumber}
            style={({ hovered }) => [
              styles.conversationItem,
              hovered && { backgroundColor: colors.border + "40" },
              isActive && { backgroundColor: colors.primary + "20" },
            ]}
            onPress={() => handleConversationPress(conv.externalNumber)}
          >
            <View style={styles.conversationRow}>
              {matchedUser ? (
                <View style={styles.userWithAvatar}>
                  {hasUnread && <View style={[styles.unreadDot, { backgroundColor: colors.primary }]} />}
                  <Avatar userId={matchedUser.id} size={28} />
                  <View style={styles.userInfo}>
                    <ThemedText
                      style={[styles.userName, isActive && { color: colors.primary }, hasUnread && styles.unreadText]}
                      numberOfLines={1}
                    >
                      {matchedUser.name}
                    </ThemedText>
                    <ThemedText style={[styles.userPhone, { color: colors.placeholder }]} numberOfLines={1}>
                      {formatPhoneNumber(conv.externalNumber)}
                    </ThemedText>
                  </View>
                </View>
              ) : (
                <View style={styles.phoneWithIndicator}>
                  {hasUnread && <View style={[styles.unreadDot, { backgroundColor: colors.primary }]} />}
                  <ThemedText
                    style={[styles.phoneNumber, isActive && { color: colors.primary }, hasUnread && styles.unreadText]}
                    numberOfLines={1}
                  >
                    {formatPhoneNumber(conv.externalNumber)}
                  </ThemedText>
                </View>
              )}
              <ThemedText style={[styles.timestamp, { color: colors.placeholder }]}>
                {formatDistanceToNow(new Date(conv.lastMessageDate), { addSuffix: false })}
              </ThemedText>
            </View>
            <ThemedText
              style={[
                styles.preview,
                { color: colors.placeholder },
                hasUnread && styles.unreadPreview,
                matchedUser && styles.previewWithAvatar,
              ]}
              numberOfLines={1}
            >
              {conv.direction === "inbound" ? "" : "You: "}
              {conv.lastMessage}
            </ThemedText>
          </Pressable>
        );
      })}
    </DrawerContentScrollView>
  );
}

const styles = StyleSheet.create({
  loadingContainer: {
    padding: 16,
    alignItems: "center",
    gap: 8,
  },
  loadingText: {
    fontSize: 13,
  },
  errorContainer: {
    padding: 16,
  },
  errorText: {
    fontSize: 13,
  },
  emptyContainer: {
    padding: 16,
  },
  emptyText: {
    fontSize: 13,
  },
  conversationItem: {
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 8,
    marginHorizontal: 4,
  },
  conversationRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 8,
  },
  phoneWithIndicator: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
    gap: 6,
  },
  userWithAvatar: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
    gap: 8,
  },
  userInfo: {
    flex: 1,
    gap: 1,
  },
  userName: {
    fontSize: 14,
    fontWeight: "500",
  },
  userPhone: {
    fontSize: 11,
  },
  unreadDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  phoneNumber: {
    fontSize: 14,
    fontWeight: "500",
    flex: 1,
  },
  unreadText: {
    fontWeight: "700",
  },
  timestamp: {
    fontSize: 11,
  },
  preview: {
    fontSize: 13,
    marginTop: 2,
    paddingLeft: 14, // Align with phone number (after unread dot space)
  },
  previewWithAvatar: {
    paddingLeft: 44, // Align with name (after unread dot + avatar + gap)
  },
  unreadPreview: {
    fontWeight: "500",
  },
});
